import React, { useState } from 'react';

/**
 * CategoryCard — premium category display card.
 *
 * Props:
 *   category       — { category_id, name, image }
 *   isActive       — boolean
 *   colorConfig    — { grad, shadow, activeShadow, iconBg, iconText }
 *   onClick        — function(category_id)
 */
const CategoryCard = ({ category, isActive, colorConfig, onClick }) => {
  const [imgErr, setImgErr] = useState(false);

  const {
    grad = 'from-primary-400 to-primary-600',
    shadow = 'hover:shadow-primary-500/20',
    activeShadow = 'shadow-primary-500/40',
    iconBg = 'bg-primary-50',
    iconText = 'text-primary-600',
  } = colorConfig || {};

  return (
    <button
      type="button"
      title={category.name}
      onClick={() => onClick(category)}
      className={`group relative flex flex-col items-center justify-between rounded-3xl text-center transition-all duration-400 ease-out
        ${isActive
          ? `bg-gradient-to-br ${grad} text-white shadow-2xl ${activeShadow} scale-[1.04] -translate-y-1`
          : `bg-white border border-gray-100 shadow-sm ${shadow} hover:shadow-xl hover:-translate-y-1.5 hover:border-transparent`
        }`}
    >
      {/* Hover Tooltip Overlay — shows full category name clearly on hover */}
      <div className="pointer-events-none absolute -top-10 left-1/2 z-30 -translate-x-1/2 scale-95 opacity-0 transition-all duration-200 group-hover:scale-100 group-hover:opacity-100">
        <div className="relative whitespace-nowrap rounded-xl bg-gray-900/95 px-3 py-1.5 text-xs font-bold text-white shadow-xl backdrop-blur-sm">
          {category.name}
          <div className="absolute -bottom-1 left-1/2 h-2 w-2 -translate-x-1/2 rotate-45 bg-gray-900/95" />
        </div>
      </div>

      {/* Subtle hover gradient overlay */}
      {!isActive && (
        <div className={`absolute inset-0 opacity-0 group-hover:opacity-[0.04] transition-opacity duration-300 bg-gradient-to-br ${grad} rounded-3xl pointer-events-none`} />
      )}

      {/* Image — full width, tall */}
      <div className={`relative w-full overflow-hidden rounded-t-3xl ${isActive ? 'bg-white/15' : iconBg}`}
           style={{ aspectRatio: '1 / 1' }}>
        {category.image && !imgErr ? (
          <img
            src={category.image}
            alt={category.name}
            className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-110"
            onError={() => setImgErr(true)}
          />
        ) : (
          <div className={`flex h-full w-full items-center justify-center`}>
            <span className={`text-4xl sm:text-5xl font-black ${isActive ? 'text-white/90' : iconText}`}>
              {category.name ? category.name.charAt(0) : ''}
            </span>
          </div>
        )}

        {/* Active glow overlay */}
        {isActive && (
          <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent" />
        )}
      </div>

      {/* Name */}
      <div className="w-full px-2 pb-3 pt-2">
        <p className={`text-xs font-bold leading-snug tracking-wide sm:text-sm line-clamp-2
          ${isActive ? 'text-white' : 'text-gray-800 group-hover:text-primary-700'}`}>
          {category.name}
        </p>
        {isActive && (
          <span className="mt-1.5 inline-block h-1 w-8 rounded-full bg-white/60" />
        )}
      </div>
    </button>
  );
};

export default CategoryCard;
